import { CalendarClock, CheckCircle2, TriangleAlert, Trash2, Loader2 } from "lucide-react";

const fmtDShort = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "");
const r2 = (n) => Math.round(n * 100) / 100;

function buildRows(detail, tab) {
  const m = {};
  const get = (d) => (m[d] = m[d] || { date: d, clocked: 0, saved: 0, parts: [] });
  (detail.punches || []).forEach((p) => {
    if (p.flagged || p.open || (tab !== "all" && p.position !== tab)) return;
    get(p.date).clocked += p.hours || 0;
  });
  (detail.saved_days || []).forEach((s) => {
    if (tab !== "all" && s.position !== tab) return;
    const row = get(s.date);
    row.saved += s.hours || 0;
    row.parts.push(s);
  });
  return Object.values(m).sort((a, b) => a.date.localeCompare(b.date));
}

function DeptTabs({ detail, tab, setTab }) {
  const saved = detail.saved_by_position || {};
  const clocked = {};
  (detail.punches || []).forEach((p) => { if (!p.flagged && !p.open) clocked[p.position] = (clocked[p.position] || 0) + (p.hours || 0); });
  const depts = Array.from(new Set([...Object.keys(saved), ...Object.keys(clocked)]));
  if (depts.length === 0) return null;
  const btn = (key, label, warn) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${tab === key ? "border-primary bg-primary text-primary-foreground" : warn ? "border-red-300 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400" : "border-border bg-primary/10 text-primary hover:bg-primary/20"}`}
      data-testid={`ps-emp-tab-${key}`}
    >
      {label}
    </button>
  );
  return (
    <div className="flex flex-wrap gap-2" data-testid="ps-emp-by-position">
      {btn("all", "All Departments")}
      {depts.map((d) => {
        const s = saved[d] || 0;
        const c = clocked[d] || 0;
        return btn(d, <>{d}: <b className="tabular-nums">{Number(s).toFixed(2)}</b> saved · <span className="tabular-nums">{c.toFixed(2)}</span> clocked</>, Math.abs(s - c) > 0.1);
      })}
    </div>
  );
}

function SavedParts({ parts, canRemove, removing, onRemove, mismatch }) {
  if (parts.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col items-end gap-1">
      {parts.map((s) => {
        const key = `${s.date}|${s.position}`;
        const orphan = !s.clock_supported;
        return (
          <div key={key} className="flex items-center gap-1.5 text-xs" data-testid={`ps-emp-part-${s.date}-${s.position}`}>
            <span className={orphan ? "font-medium text-red-600 dark:text-red-400" : "text-muted-foreground"}>{s.position}</span>
            <span className="tabular-nums font-medium">{Number(s.hours).toFixed(2)}</span>
            {orphan && <span className="rounded-sm bg-red-100 px-1 py-px text-[0.6rem] font-semibold uppercase text-red-700 dark:bg-red-950/40 dark:text-red-400">no clock-in</span>}
            {orphan && s.override_reason && (
              <span className="max-w-[12rem] truncate rounded-sm bg-amber-100 px-1 py-px text-[0.6rem] text-amber-800 dark:bg-amber-950/40 dark:text-amber-300" title={`${s.override_reason} — ${s.override_by || ""}`} data-testid={`ps-emp-reason-${s.date}-${s.position}`}>
                Reason: {s.override_reason}
              </span>
            )}
            {orphan && mismatch && canRemove && (
              <button
                type="button"
                onClick={() => onRemove(s)}
                disabled={!!removing}
                className="inline-flex items-center gap-0.5 rounded-sm border border-red-300 px-1.5 py-px text-[0.65rem] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:text-red-400"
                data-testid={`ps-emp-remove-${s.date}-${s.position}`}
              >
                {removing === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Remove
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

export const EmpDeptView = ({ detail, tab, setTab, canRemove, removing, onRemove }) => {
  const rows = buildRows(detail, tab);
  return (
    <>
      <DeptTabs detail={detail} tab={tab} setTab={setTab} />
      {rows.length > 0 && (
        <div>
          <h3 className="mb-2 flex items-center gap-2 text-sm font-bold"><CalendarClock className="h-4 w-4" /> Hours by Day{tab !== "all" ? ` — ${tab}` : ""} <span className="text-xs font-normal text-muted-foreground">(saved payroll by department vs clocked — mismatches in red)</span></h3>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm" data-testid="ps-emp-byday">
              <thead>
                <tr className="bg-muted/50 text-left text-[0.65rem] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2 text-right">Saved by Dept</th>
                  <th className="px-3 py-2 text-right">Saved</th>
                  <th className="px-3 py-2 text-right">Clocked</th>
                  <th className="px-3 py-2 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const saved = r2(r.saved);
                  const clocked = r2(r.clocked);
                  const match = Math.abs(saved - clocked) <= 0.1;
                  return (
                    <tr key={r.date} className={`border-t border-border align-top ${match ? "" : "bg-red-50 dark:bg-red-950/20"}`} data-testid={`ps-emp-byday-${r.date}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{fmtDShort(r.date)}</td>
                      <td className="px-3 py-2 text-right"><SavedParts parts={r.parts} canRemove={canRemove} removing={removing} onRemove={onRemove} mismatch={!match} /></td>
                      <td className={`px-3 py-2 text-right tabular-nums font-medium ${match ? "" : "text-red-600 dark:text-red-400"}`}>{saved.toFixed(2)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${match ? "text-muted-foreground" : "font-medium text-red-600 dark:text-red-400"}`}>{clocked.toFixed(2)}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        {match
                          ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> OK</span>
                          : <span className="inline-flex items-center gap-1 font-semibold text-red-600 dark:text-red-400"><TriangleAlert className="h-3.5 w-3.5" /> {(saved - clocked > 0 ? "+" : "") + (saved - clocked).toFixed(2)}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!canRemove && detail.status === "submitted" && (
            <p className="mt-1.5 text-[0.65rem] text-muted-foreground">This pay period has been run — reopen it to remove duplicate hours.</p>
          )}
        </div>
      )}
    </>
  );
};
